import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { EyeInvisibleOutlined, EyeTwoTone, KeyOutlined, MailOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Form, Input, Result, Spin } from "antd";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Trans } from "@devowl-wp/react-frontend";
import { useMagicLinkRequest } from "../../../../hooks/useMagicLinkRequest.js";
import { useMagicLinkSessionRequest } from "../../../../hooks/useMagicLinkSessionRequest.js";
import { useSessionByPasswordLoginRequest } from "../../../../hooks/useSessionByPasswordLoginRequest.js";
import { useTranslation } from "../../../../others/i18n.js";
import { useStores } from "../../../../store/root.js";
import { spacing } from "../../../../styles/spacing.js";
import { ESessionState } from "../../../../types/store/authentication.js";
import { QueryKeys } from "../../../../util/query-keys.js";
/**
 * Login form.
 * Here a user can request a magic link email to get logged in.
 */ const Login = observer(()=>{
    // Commons
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    // Get params to retrieve  magic link token
    const [search] = useSearchParams();
    const magicLinkToken = search.get("magic-link-token");
    // Login process
    const [magicLinkEmail, setMagicLinkEmail] = useState();
    const [email, setEmail] = useState();
    const [password, setPassword] = useState();
    const [isPasswordLogin, setPasswordLogin] = useState(false);
    const useMagicLink = useMagicLinkRequest(magicLinkEmail);
    const useMagicLinkSession = useMagicLinkSessionRequest(magicLinkToken);
    const usePasswordLoginRequest = useSessionByPasswordLoginRequest(email, password);
    const { rcbAuthenticationStore, notificationStore, rcbUserSettingsStore } = useStores();
    const { sessionState } = rcbAuthenticationStore;
    const [form] = Form.useForm();
    const onFormSubmit = useCallback(async ({ email, password })=>{
        notificationStore.clearAll(); // clear previous errors on submit
        queryClient.invalidateQueries(QueryKeys.createMagicLinkRequest(email));
        queryClient.invalidateQueries(QueryKeys.createSessionByPasswordLogin(email));
        if (password) {
            setEmail(email);
            setPassword(password);
        } else {
            setMagicLinkEmail(email);
        }
    }, [
        magicLinkEmail
    ]);
    const inputRef = useRef(undefined);
    useEffect(()=>{
        if (sessionState === ESessionState.Stored && !magicLinkToken || magicLinkToken && useMagicLinkSession.isFetched && !useMagicLinkSession.isError) {
            const userLoggedIn = rcbAuthenticationStore.session.user;
            rcbUserSettingsStore.updateSettings(rcbUserSettingsStore.loadSettings(userLoggedIn));
            navigate("/rcb");
        }
        if (useMagicLinkSession.isError && sessionState === ESessionState.None) {
            notificationStore.add({
                type: "error",
                message: t("Login failed. Please request a new magic link token."),
                duration: 5,
                closable: true
            });
        } else if (usePasswordLoginRequest.isError && sessionState === ESessionState.None && !!password) {
            notificationStore.addFromServerError(usePasswordLoginRequest.error, t);
            setPassword(undefined);
        }
    }, [
        sessionState,
        useMagicLinkSession,
        usePasswordLoginRequest
    ]);
    useEffect(()=>{
        if (useMagicLink.isError) {
            notificationStore.addFromServerError(useMagicLink.error, t);
            inputRef.current.select();
        }
    }, [
        useMagicLink
    ]);
    const spinTip = useMagicLink.isFetching ? t("Sending Mail ...") : useMagicLinkSession.isFetching ? t("Logging in ...") : "";
    const buttonMarginStyle = {
        marginRight: spacing.layout.content.marginMedium
    };
    const isMagicLinkRequested = useMagicLink.isFetched && !useMagicLink.isError;
    return /*#__PURE__*/ _jsx("div", {
        className: "real-commerce-magic-link-form",
        children: /*#__PURE__*/ _jsxs(Spin, {
            spinning: useMagicLink.isFetching || useMagicLinkSession.isFetching,
            tip: spinTip,
            children: [
                /*#__PURE__*/ _jsx("p", {
                    children: useMagicLinkSession.isFetched ? JSON.stringify(useMagicLinkSession.data) : ""
                }),
                /*#__PURE__*/ _jsx("span", {
                    style: {
                        fontSize: 25,
                        fontWeight: "bold"
                    },
                    children: t("Sign in to Real Cookie Banner backend")
                }),
                /*#__PURE__*/ _jsx("p", {
                    children: t("Manage service- and content blocker templates. Get logged in by requesting a magic link via email.")
                }),
                magicLinkEmail && !useMagicLink.isError && useMagicLink.data ? /*#__PURE__*/ _jsx(_Fragment, {
                    children: /*#__PURE__*/ _jsx(Result, {
                        title: t("Email successfully sent!"),
                        subTitle: /*#__PURE__*/ _jsxs(Trans, {
                            children: [
                                "Sent magic link to ",
                                /*#__PURE__*/ _jsx("strong", {
                                    children: magicLinkEmail
                                }),
                                ". Please check your inbox!"
                            ]
                        }),
                        status: "success"
                    })
                }) : /*#__PURE__*/ _jsxs(Form, {
                    form: form,
                    onFinish: onFormSubmit,
                    labelCol: {
                        span: 0
                    },
                    initialValues: {
                        email: ""
                    },
                    children: [
                        /*#__PURE__*/ _jsx(Form.Item, {
                            name: "email",
                            rules: [
                                {
                                    type: "email",
                                    required: true,
                                    message: t("Please enter a valid email address!")
                                }
                            ],
                            children: /*#__PURE__*/ _jsx(Input, {
                                size: "large",
                                style: {
                                    maxWidth: 500
                                },
                                prefix: /*#__PURE__*/ _jsx(MailOutlined, {
                                    style: {
                                        opacity: 0.5,
                                        marginRight: 15
                                    }
                                }),
                                placeholder: t("Your email address..."),
                                disabled: isMagicLinkRequested,
                                ref: inputRef
                            })
                        }),
                        /*#__PURE__*/ _jsx(Form.Item, {
                            name: "password",
                            hidden: !isPasswordLogin,
                            rules: isPasswordLogin ? [
                                {
                                    type: "string",
                                    required: true,
                                    message: t("Please enter a password!")
                                }
                            ] : [],
                            children: /*#__PURE__*/ _jsx(Input.Password, {
                                status: usePasswordLoginRequest.isError ? "error" : undefined,
                                size: "large",
                                style: {
                                    maxWidth: 500
                                },
                                placeholder: t("Password"),
                                prefix: /*#__PURE__*/ _jsx(KeyOutlined, {
                                    style: {
                                        opacity: 0.5,
                                        marginRight: 15
                                    }
                                }),
                                iconRender: (visible)=>visible ? /*#__PURE__*/ _jsx(EyeTwoTone, {}) : /*#__PURE__*/ _jsx(EyeInvisibleOutlined, {})
                            })
                        }),
                        /*#__PURE__*/ _jsx(Form.Item, {
                            hidden: !isPasswordLogin,
                            children: /*#__PURE__*/ _jsx(Button, {
                                size: "large",
                                type: "primary",
                                hidden: !isPasswordLogin,
                                disabled: usePasswordLoginRequest.isFetching,
                                htmlType: "submit",
                                children: t("Login")
                            })
                        }),
                        /*#__PURE__*/ _jsxs(Form.Item, {
                            hidden: isPasswordLogin,
                            children: [
                                /*#__PURE__*/ _jsx(Button, {
                                    size: "large",
                                    disabled: isMagicLinkRequested,
                                    onClick: ()=>setPasswordLogin(true),
                                    style: buttonMarginStyle,
                                    htmlType: "button",
                                    children: t("Log in by password")
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    type: "primary",
                                    size: "large",
                                    disabled: isMagicLinkRequested,
                                    style: buttonMarginStyle,
                                    htmlType: "submit",
                                    children: t("Send magic link")
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
});
export { Login };
